import React from 'react';
import { ESFRIANDO_PADRAO, LEAD_ORIGINS, LIMITE_ESFRIANDO, STAGES } from './constantes';
import { addDaysISO, calcContractTotal } from './formato';

/**
 * Etapas cujo VALOR não entra no pipeline. Fechado já virou receita, perdido saiu, e
 * repescagem é dinheiro já dado como perdido: somar de novo infla a previsão conforme a
 * fila cresce. Fica aqui em cima porque a regra vale para os KPIs E para os três funis.
 */
const FORA_DO_PIPELINE = ['fechado', 'perdido', 'repescagem'];

/**
 * Etapas que contam como PERDA para efeito de histórico. Um lead em repescagem foi perdido
 * de verdade; some dessa conta e o win rate sobe sozinho, sem ninguém ter fechado nada.
 */
const CONTA_COMO_PERDA = ['perdido', 'repescagem'];

/** repescar_em só vale em ISO. '08/09/2026' compararia como menor que hoje e repescaria na hora. */
const DATA_ISO = /^\d{4}-\d{2}-\d{2}$/;

function diasNaEtapa(lead) {
  const ref = lead.stage_changed_at || lead.updated_at || lead.created_at;
  if (!ref || ['fechado','perdido','repescagem'].includes(lead.stage)) return null;
  return Math.floor((Date.now() - new Date(ref).getTime()) / 86400000);
}

/** null quando saudável; senão o nível de esfriamento. */

function statusEsfriando(lead) {
  const dias = diasNaEtapa(lead);
  if (dias === null) return null;
  const limite = LIMITE_ESFRIANDO[lead.stage] ?? ESFRIANDO_PADRAO;
  if (dias < limite) return null;
  const nivel = dias >= limite * 2 ? 'frio' : 'esfriando';
  return {
    dias, limite, nivel,
    label: nivel === 'frio' ? 'Parado' : 'Esfriando',
    emoji: nivel === 'frio' ? '🧊' : '⏳',
    cls: nivel === 'frio'
      ? 'bg-red-50 text-red-600 border-red-200'
      : 'bg-amber-50 text-amber-700 border-amber-200',
  };
}

/** C-69: dias entre a criação do lead e a primeira interação registrada. */

function tempoPrimeiroContato(lead) {
  if (!lead.created_at || !lead.interactions?.length) return null;
  const datas = lead.interactions
    .map(i => i.created_at || (i.date ? i.date + 'T12:00:00' : null))
    .filter(Boolean)
    .sort();
  if (!datas.length) return null;
  const h = (new Date(datas[0]) - new Date(lead.created_at)) / 3600000;
  return h < 0 ? 0 : Number(h.toFixed(1));
}

// Normaliza URL de proposta: aceita colar sem protocolo e bloqueia esquemas perigosos

function computeKPIs(leads) {
  // Dinheiro: repescagem fica FORA. É valor já dado como perdido; somar de novo infla a
  // previsão sozinha conforme a fila de repescagem cresce. Volta inteiro quando o lead
  // sai da repescagem para uma etapa de verdade.
  const emPipeline = leads.filter(l => ['backlog','prospeccao','em_contato','qualificacao','aguardando','proposta','criando_proposta','negociacao'].includes(l.stage)||!l.stage);
  const repescagem = leads.filter(l => l.stage === 'repescagem');
  // Presença: repescagem conta como ativo, porque voltou a ser trabalho.
  const active   = [...emPipeline, ...repescagem];
  const closed   = leads.filter(l => l.stage === 'fechado');
  const lost     = leads.filter(l => l.stage === 'perdido');
  // 'repescagem' precisa ficar nos denominadores: sem ela, o lead sai da conta ao ser
  // repescado e o win rate sobe sozinho, sem ninguém ter fechado nada.
  const qualified = leads.filter(l => ['qualificacao','aguardando','proposta','criando_proposta','negociacao','enviar_contrato','aguardando_assinatura','fechado','perdido','repescagem'].includes(l.stage));
  const proposals = leads.filter(l => ['proposta','criando_proposta','negociacao','enviar_contrato','aguardando_assinatura','fechado','perdido','repescagem'].includes(l.stage));
  const pipeline_value          = emPipeline.reduce((s,l) => s+(Number(l.value)||0), 0);
  const closed_value            = closed.reduce((s,l) => s+(Number(l.value)||0), 0);
  const pipeline_total_contract = emPipeline.reduce((s,l) => s + calcContractTotal(l.value, l.recurrence), 0);
  const closed_total_contract   = closed.reduce((s,l) => s + calcContractTotal(l.value, l.recurrence), 0);
  const win_rate       = qualified.length > 0 ? (closed.length / qualified.length * 100) : 0;
  const proposal_cr    = proposals.length > 0 ? (closed.length / proposals.length * 100)  : 0;
  const avg_ticket     = closed.length > 0 ? closed_total_contract / closed.length : 0;
  let avg_lead_time = 0;
  if (closed.length > 0) {
    const times = closed.map(l => Math.max(0, Math.round((new Date(l.updated_at) - new Date(l.created_at)) / 86400000)));
    avg_lead_time = times.reduce((s,t) => s+t, 0) / times.length;
  }
  const by_stage = STAGES.map(s => ({
    key: s.key, label: s.label,
    count: leads.filter(l => l.stage === s.key).length,
    value: leads.filter(l => l.stage === s.key).reduce((sum,l) => sum+(Number(l.value)||0), 0),
    total_contract: leads.filter(l => l.stage === s.key).reduce((sum,l) => sum + calcContractTotal(l.value, l.recurrence), 0)
  }));
  // C-69: tempo médio até o primeiro contato
  const tempos_ptc = leads.map(tempoPrimeiroContato).filter(v => v !== null);
  const avg_primeiro_contato = tempos_ptc.length
    ? tempos_ptc.reduce((s,v) => s+v, 0) / tempos_ptc.length : 0;
  const sem_primeiro_contato = leads.filter(l =>
    !['fechado','perdido','repescagem'].includes(l.stage) && !l.interactions?.length).length;

  // C-54: pipeline ponderado pela probabilidade informada
  const weighted_pipeline = emPipeline.reduce((s,l) => {
    const prob = l.forecast_prob === null || l.forecast_prob === undefined ? 50 : Number(l.forecast_prob);
    return s + (Number(l.value)||0) * (prob/100);
  }, 0);

  // C-33: quantos estão esfriando agora
  const esfriando_count = leads.filter(l => statusEsfriando(l)).length;

  const today = new Date(); today.setHours(0,0,0,0);
  const next_contacts = leads
    .filter(l => l.next_contact_date && !['fechado','perdido'].includes(l.stage))
    .map(l => {
      const d = new Date(l.next_contact_date+'T00:00:00');
      const diff = Math.round((d - today) / 86400000);
      return { id:l.id, company_name:l.company_name, next_contact_date:l.next_contact_date, diff, status: diff<0?'overdue':diff===0?'today':'upcoming' };
    })
    .sort((a,b) => a.diff - b.diff);
  return { total_leads:leads.length, active_count:active.length, closed_count:closed.length, lost_count:lost.length,
           pipeline_value, closed_value, pipeline_total_contract, closed_total_contract,
           win_rate, proposal_conversion_rate:proposal_cr,
           avg_lead_time_days:avg_lead_time, avg_ticket, by_stage, next_contacts,
           avg_primeiro_contato, sem_primeiro_contato, weighted_pipeline, esfriando_count,
           repescagem_count: repescagem.length };
}

// ─── Funil por Origem ─────────────────────────────────────

function computeOriginFunnel(leads) {
  const allOriginKeys = [...LEAD_ORIGINS.map(o => o.key), '__none__'];
  return allOriginKeys.map(originKey => {
    const origin = LEAD_ORIGINS.find(o => o.key === originKey);
    const hasNoOrigin = l => !l.origem || l.origem === '';
    const hasPartner  = l => l.partners?.length > 0;
    const originLeads = originKey === '__none__'
      ? leads.filter(l => hasNoOrigin(l) && !hasPartner(l))
      : originKey === 'parceiro'
        ? leads.filter(l => l.origem === 'parceiro' || (hasNoOrigin(l) && hasPartner(l)))
        : leads.filter(l => l.origem === originKey);
    if (originLeads.length === 0) return null;
    const total = originLeads.length;
    const qualifOrAbove = originLeads.filter(l =>
      ['qualificacao','aguardando','proposta','criando_proposta','negociacao',
       'enviar_contrato','aguardando_assinatura','fechado'].includes(l.stage)
    ).length;
    const propOrAbove = originLeads.filter(l =>
      ['proposta','criando_proposta','negociacao','enviar_contrato',
       'aguardando_assinatura','fechado'].includes(l.stage)
    ).length;
    const fechado = originLeads.filter(l => l.stage === 'fechado').length;
    const perdido = originLeads.filter(l => l.stage === 'perdido').length;
    const activeLeads  = originLeads.filter(l => !FORA_DO_PIPELINE.includes(l.stage));
    const closedLeads  = originLeads.filter(l => l.stage === 'fechado');
    const pipelineValue = activeLeads.reduce((s,l) => s + (Number(l.value)||0), 0);
    const closedValue   = closedLeads.reduce((s,l) => s + (Number(l.value)||0), 0);
    const closedContractValue = closedLeads.reduce((s,l) => s + calcContractTotal(l.value, l.recurrence), 0);
    return {
      key: originKey,
      label: origin ? origin.label : 'Sem origem',
      emoji: origin ? origin.emoji : '❓',
      total, qualifOrAbove, propOrAbove, fechado, perdido,
      pipelineValue, closedValue, closedContractValue,
      qualifRate: total > 0 ? (qualifOrAbove / total * 100) : 0,
      propRate:   total > 0 ? (propOrAbove   / total * 100) : 0,
      winRate: qualifOrAbove > 0 ? (fechado / qualifOrAbove * 100) : 0,
    };
  }).filter(Boolean).sort((a,b) => b.total - a.total);
}

function computeSellerFunnel(leads, sellers) {
  const allSellers = [
    ...sellers.map(s => ({ id: s.id, name: s.name, meta: s.goal_monthly })),
    { id: '__none__', name: 'Sem vendedor', meta: 0 }
  ];
  return allSellers.map(seller => {
    const sl = seller.id === '__none__'
      ? leads.filter(l => !l.seller_id)
      : leads.filter(l => l.seller_id === seller.id);
    if (sl.length === 0) return null;
    const total = sl.length;
    const qualifOrAbove = sl.filter(l =>
      ['qualificacao','aguardando','proposta','criando_proposta','negociacao',
       'enviar_contrato','aguardando_assinatura','fechado'].includes(l.stage)
    ).length;
    const propOrAbove = sl.filter(l =>
      ['proposta','criando_proposta','negociacao','enviar_contrato',
       'aguardando_assinatura','fechado'].includes(l.stage)
    ).length;
    const fechado = sl.filter(l => l.stage === 'fechado').length;
    const perdido = sl.filter(l => l.stage === 'perdido').length;
    const activeLeads = sl.filter(l => !FORA_DO_PIPELINE.includes(l.stage));
    const closedLeads = sl.filter(l => l.stage === 'fechado');
    const pipelineValue       = activeLeads.reduce((s,l) => s + (Number(l.value)||0), 0);
    const closedValue         = closedLeads.reduce((s,l) => s + (Number(l.value)||0), 0);
    const closedContractValue = closedLeads.reduce((s,l) => s + calcContractTotal(l.value, l.recurrence), 0);
    return {
      key: seller.id, label: seller.name,
      meta: Number(seller.meta) || 0,
      total, qualifOrAbove, propOrAbove, fechado, perdido,
      pipelineValue, closedValue, closedContractValue,
      qualifRate: total > 0 ? (qualifOrAbove / total * 100) : 0,
      propRate:   total > 0 ? (propOrAbove   / total * 100) : 0,
      winRate: qualifOrAbove > 0 ? (fechado / qualifOrAbove * 100) : 0,
    };
  }).filter(Boolean).sort((a,b) => b.closedValue - a.closedValue);
}

function computePartnerDashboard(leads, partners) {
  return partners.map(p => {
    const pLeads = leads.filter(l => l.partners?.some(lp => lp.id === p.id));
    const total_pipeline          = pLeads.filter(l => !FORA_DO_PIPELINE.includes(l.stage)).reduce((s,l) => s+(Number(l.value)||0), 0);
    const total_closed            = pLeads.filter(l => l.stage==='fechado').reduce((s,l) => s+(Number(l.value)||0), 0);
    const pipeline_total_contract = pLeads.filter(l => !FORA_DO_PIPELINE.includes(l.stage)).reduce((s,l) => s + calcContractTotal(l.value, l.recurrence), 0);
    const closed_total_contract   = pLeads.filter(l => l.stage==='fechado').reduce((s,l) => s + calcContractTotal(l.value, l.recurrence), 0);
    const total_lost     = pLeads.filter(l => CONTA_COMO_PERDA.includes(l.stage)).length;
    // Comissão (roadmap #6): sobre o contrato total dos fechados; paga = partners[].paid_at preenchido
    const comissaoDe = (l) => { const r = Number(l.partners?.find(x=>x.id===p.id)?.commission_rate) || 0; return r ? calcContractTotal(l.value, l.recurrence) * r / 100 : 0; };
    const fechados = pLeads.filter(l => l.stage==='fechado');
    const pagoEm = (l) => l.partners?.find(x=>x.id===p.id)?.paid_at;
    const commission_paid = fechados.filter(l =>  pagoEm(l)).reduce((s,l) => s + comissaoDe(l), 0);
    const commission_due  = fechados.filter(l => !pagoEm(l)).reduce((s,l) => s + comissaoDe(l), 0);
    const by_stage = STAGES.map(s => { const sl = pLeads.filter(l=> s.key==='backlog'?(l.stage==='backlog'||l.stage==='prospeccao'||!l.stage):l.stage===s.key); return { stage:s.key, label:s.label, count:sl.length, value:sl.reduce((sum,l)=>sum+(Number(l.value)||0),0), total_contract:sl.reduce((sum,l)=>sum+calcContractTotal(l.value,l.recurrence),0) }; });
    const closed_count    = pLeads.filter(l=>l.stage==='fechado').length;
    const qualified_count = pLeads.filter(l=>['qualificacao','proposta','criando_proposta','negociacao','enviar_contrato','aguardando_assinatura','fechado','perdido','repescagem'].includes(l.stage)).length;
    const win_rate = qualified_count > 0 ? (closed_count / qualified_count * 100) : 0;
    return { partner:p, total_leads:pLeads.length, total_pipeline, total_closed, pipeline_total_contract, closed_total_contract, total_lost, by_stage, win_rate, leads:pLeads, commission_paid, commission_due };
  });
}

// ─── Componentes Base ─────────────────────────────────────

/**
 * Próxima abordagem de um lead com cadência, contada a partir de `base`
 * (data da interação ou hoje). Sem cadência, ou em etapa final, devolve null:
 * quem chama mantém o comportamento antigo.
 */
function proximaAbordagem(lead, base) {
  const dias = Number(lead?.followup_days) || 0;
  if (!dias || ['fechado', 'perdido'].includes(lead?.stage)) return null;
  return addDaysISO(base || null, dias);
}

/**
 * Patch derivado da troca de etapa, num lugar só. O formulário e o arrastar do Kanban
 * chamam daqui: enquanto essa regra viveu duplicada nos dois handlers, ela não tinha teste
 * e divergia na primeira manutenção. `agora` é ISO; devolve só os campos que dependem da
 * etapa, nunca os que vêm do formulário.
 */
function aoTrocarEtapa(lead, novaEtapa, agora, dados) {
  const anterior = lead?.stage || 'backlog';
  const patch = { stage_changed_at: agora };
  if (novaEtapa === 'fechado') patch.closed_at = agora;
  else if (anterior === 'fechado') patch.closed_at = null;
  // Etapa final encerra o ciclo de follow-up. Repescagem NÃO é final: ela vive da data.
  if (['fechado', 'perdido'].includes(novaEtapa)) { patch.next_contact_date = null; patch.followup_days = null; }
  // Data real da perda, que nada depois reescreve. A promoção mexe em stage_changed_at, e
  // era por ele que a tela de Concluídas datava a perda: sem este carimbo, uma perda de
  // janeiro migrava para o mês atual sozinha no dia em que o lead fosse repescado.
  if (novaEtapa === 'perdido') patch.lost_at = agora;
  // repescar_em só faz sentido enquanto o lead está perdido. Sair de perdido por qualquer
  // porta limpa a data, senão o lead guarda uma data velha e se auto-promove meses depois.
  if (novaEtapa !== 'perdido' && lead?.repescar_em) patch.repescar_em = null;
  // Ao entrar na repescagem a data marcada vira a próxima abordagem, e o campo é consumido.
  // É daí que vêm de graça o sino, o badge de data e a borda do card.
  if (novaEtapa === 'repescagem') {
    // addDaysISO e NUNCA agora.slice(0,10): toISOString() já vira o dia seguinte depois
    // das 21h no Brasil, e o lead nasceria agendado para amanhã, sem piscar e sem sino.
    // A data que veio do formulário manda: quem move o lead já escolheu.
    patch.next_contact_date = dados?.next_contact_date || lead?.repescar_em
                           || lead?.next_contact_date || addDaysISO(null, 0);
    patch.repescar_em = null;
  }
  return patch;
}

/**
 * Leads perdidos cuja data de repescagem já venceu. `<=` e nunca `===`: data que caiu num
 * fim de semana ou nas férias não pode sumir para sempre.
 */
function paraRepescar(leads, hoje) {
  const dia = hoje || addDaysISO(null, 0);
  return (leads || []).filter(l =>
    l.stage === 'perdido' && DATA_ISO.test(l.repescar_em || '') && l.repescar_em <= dia);
}

/** Repescagem que ainda não foi tocada: é ela que pisca no Kanban. */
function repescagemAberta(lead, hoje) {
  if (lead?.stage !== 'repescagem' || !lead.next_contact_date) return false;
  return lead.next_contact_date <= (hoje || addDaysISO(null, 0));
}

/**
 * Próximos passos abertos de todos os leads (roadmap #8), com o lead de origem,
 * ordenados por prazo: atrasados primeiro, sem prazo por último.
 */
function tarefasAbertas(leads) {
  const hoje = addDaysISO(null, 0);
  return (leads || [])
    .flatMap(l => (l.tasks || []).filter(t => !t.done).map(t => ({
      ...t, lead_id: l.id, lead_name: l.company_name,
      atrasada: !!t.due && t.due < hoje, hoje: t.due === hoje,
    })))
    .sort((a, b) => (a.due || '9999').localeCompare(b.due || '9999'));
}

export { CONTA_COMO_PERDA, FORA_DO_PIPELINE, aoTrocarEtapa, computeKPIs, computeOriginFunnel, computePartnerDashboard, computeSellerFunnel, diasNaEtapa, paraRepescar, proximaAbordagem, repescagemAberta, statusEsfriando, tarefasAbertas, tempoPrimeiroContato };
